/**
 * Application-wide constants for SafeLine Admin Dashboard
 */

// User Roles
export const USER_ROLES = {
  DEVELOPER: 'developer',
  ADMIN: 'school_admin',
  MODERATOR: 'school_moderator',
  TEACHER: 'teacher'
};

// Permission levels
export const PERMISSIONS = {
  VIEW_DASHBOARD: 'view_dashboard',
  MANAGE_USERS: 'manage_users',
  APPROVE_GUARDIANS: 'approve_guardians',
  VIEW_REPORTS: 'view_reports',
  EXPORT_DATA: 'export_data',
  SYSTEM_SETTINGS: 'system_settings',
  VERIFY_PICKUP: 'verify_pickup'
};

// Status values
export const STATUS = {
  PENDING: 'pending',
  APPROVED: 'approved',
  DENIED: 'denied',
  COMPLETED: 'completed',
  ARRIVED: 'arrived',
  CANCELLED: 'cancelled',
  REJECTED: 'rejected'
};

// API endpoints (for real implementation)
export const API_ENDPOINTS = {
  LOGIN: '/api/auth/login',
  LOGIN_WITH_PIN: '/api/auth/login-pin',
  LOGOUT: '/api/auth/logout',
  USERS: '/api/users',
  STUDENTS: '/api/students',
  GUARDIANS: '/api/guardians',
  GUESTS: '/api/guests',
  PICKUP_LOGS: '/api/pickup-logs',
  NOTIFICATIONS: '/api/notifications',
  SCHOOLS: '/api/schools',
  SETTINGS: '/api/settings',
  WALKING_SCHEDULES: '/api/walking-schedules',
  GUARDIAN_TAGS: '/api/guardian-tags',
  STATS: '/api/stats'
};

// Local storage keys
export const STORAGE_KEYS = {
  AUTH_TOKEN: 'safeline_auth_token',
  USER_DATA: 'safeline_user_data',
  THEME_PREFERENCE: 'safeline_theme',
  LAST_SCHOOL: 'safeline_last_school'
};

// Notification types
export const NOTIFICATION_TYPES = {
  GUEST_ARRIVAL: 'guest_arrival',
  GUARDIAN_APPROVED: 'guardian_approved',
  GUARDIAN_DENIED: 'guardian_denied',
  PICKUP_COMPLETED: 'pickup_completed',
  SYSTEM_ALERT: 'system_alert'
};

// Time constants
export const TIME_CONSTANTS = {
  TOKEN_REFRESH_INTERVAL: 1000 * 60 * 15, // 15 minutes
  SESSION_TIMEOUT: 1000 * 60 * 60, // 1 hour
  NOTIFICATION_POLL_INTERVAL: 1000 * 10, // 10 seconds
  DEFAULT_DEBOUNCE_DELAY: 300, // 300 milliseconds
  DEFAULT_THROTTLE_DELAY: 500, // 500 milliseconds
  CLIPBOARD_FEEDBACK_TIMEOUT: 2000 // 2 seconds
};

// Error messages
export const ERROR_MESSAGES = {
  GENERIC: 'An error occurred. Please try again.',
  AUTH_FAILED: 'Authentication failed. Please check your credentials.',
  SESSION_EXPIRED: 'Your session has expired. Please log in again.',
  PERMISSION_DENIED: 'You do not have permission to perform this action.',
  NETWORK_ERROR: 'Network error. Please check your connection.',
  SERVER_ERROR: 'Server error. Please try again later.'
};

// Success messages
export const SUCCESS_MESSAGES = {
  LOGIN: 'Login successful!',
  LOGOUT: 'Logout successful!',
  GUARDIAN_APPROVED: 'Guardian approved successfully!',
  GUARDIAN_DENIED: 'Guardian denied successfully!',
  SETTINGS_SAVED: 'Settings saved successfully!'
};

// Validation patterns
export const VALIDATION_PATTERNS = {
  EMAIL: /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/,
  PASSWORD: /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)[a-zA-Z\d]{8,}$/,
  PHONE: /^\(\d{3}\) \d{3}-\d{4}$/,
  LICENSE_PLATE: /^[A-Z0-9]{1,8}$/,
  PIN: /^\d{4}$/
};

// Default pagination settings
export const PAGINATION = {
  DEFAULT_PAGE_SIZE: 10,
  PAGE_SIZE_OPTIONS: [5, 10, 25, 50, 100]
};

// Date formats
export const DATE_FORMATS = {
  DISPLAY: 'MMM D, YYYY h:mm A',
  API: 'YYYY-MM-DD HH:mm:ss',
  SHORT_DATE: 'MM/DD/YYYY',
  TIME_ONLY: 'h:mm A'
};

// Breakpoints for responsive design (matches MUI defaults)
export const BREAKPOINTS = {
  XS: 0,
  SM: 576,
  MD: 768,
  LG: 992,
  XL: 1200,
  XXL: 1600
};

// Pickup Methods
export const PICKUP_METHODS = {
  LICENSE_PLATE: 'license_plate',
  TAGS: 'tags',
  WALKING: 'walking',
  MANUAL_ENTRY: 'manual_entry',
  BOTH: 'both'
};

// Walking Schedule Types
export const WALKING_SCHEDULE_TYPES = {
  DAILY: 'daily',
  WEEKLY: 'weekly',
  SPECIFIC_DATES: 'specific_dates',
  ONE_TIME: 'one_time'
};

// Tag Types
export const TAG_TYPES = {
  PHYSICAL: 'physical',
  DIGITAL: 'digital',
  QR_CODE: 'qr_code'
};

// Device Types
export const DEVICE_TYPES = {
  TABLET: 'tablet',
  PHONE: 'phone',
  DESKTOP: 'desktop'
};

// Session Types
export const SESSION_TYPES = {
  TEACHER_MONITOR: 'teacher_monitor',
  ADMIN_PORTAL: 'admin_portal',
  MODERATOR_PORTAL: 'moderator_portal'
};

// Contact Types
export const CONTACT_TYPES = {
  SUPPORT: 'support',
  FEATURE_REQUEST: 'feature_request',
  BUG_REPORT: 'bug_report',
  BILLING: 'billing'
};

// Contact Priority
export const CONTACT_PRIORITY = {
  LOW: 'low',
  NORMAL: 'normal',
  HIGH: 'high',
  URGENT: 'urgent'
};

// Contact Status
export const CONTACT_STATUS = {
  OPEN: 'open',
  IN_PROGRESS: 'in_progress',
  RESOLVED: 'resolved',
  CLOSED: 'closed'
};

// Days of the week
export const DAYS_OF_WEEK = {
  MONDAY: 'monday',
  TUESDAY: 'tuesday',
  WEDNESDAY: 'wednesday',
  THURSDAY: 'thursday',
  FRIDAY: 'friday',
  SATURDAY: 'saturday',
  SUNDAY: 'sunday'
};
